import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import request from "../../../utilities/requests";
import { subscribeToChat } from "../chatSocket";

const nameOf = (person) =>
  [person?.firstName, person?.lastName ? `${person.lastName}.` : null]
    .filter(Boolean)
    .join(" ");

const ThreadList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [available, setAvailable] = React.useState(false);
  const [threads, setThreads] = React.useState([]);
  const [events, setEvents] = React.useState([]);

  const load = React.useCallback(async () => {
    const result = await request("eventchat/threads");
    if (result?.success) {
      setAvailable(!!result.available);
      setThreads(result.threads || []);
      setEvents(result.events || []);
    }
    setLoading(false);
  }, []);

  React.useEffect(() => {
    load();
    return subscribeToChat({
      onMessage: load,
      onUpdated: load,
      onMatched: load,
      onConnect: load,
    });
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!available) {
    return (
      <Box sx={{ px: 3, pt: 6, textAlign: "center" }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {t("chat.closed.title")}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 3 }}>
          {t("chat.closed.body")}
        </Typography>
        <Button variant="outlined" onClick={() => navigate("/")}>
          {t("setup.blocked.myevents")}
        </Button>
      </Box>
    );
  }

  const eventName = (thread) =>
    events.length > 1
      ? events.find((e) => String(e.eventId) === String(thread.eventId))?.eventName
      : null;

  const previewOf = (thread) => {
    if (thread.isNew || !thread.lastMessageText) return t("chat.list.newmatch");
    return thread.lastMessageMine
      ? `${t("chat.list.you")}: ${thread.lastMessageText}`
      : thread.lastMessageText;
  };

  return (
    <Box sx={{ pb: 10 }}>
      <Typography variant="h5" sx={{ px: 2, pt: 1, pb: 1 }}>
        {t("chat.title")}
      </Typography>
      {events.length === 1 && (
        <Typography sx={{ px: 2, fontSize: 13, color: "text.secondary" }}>
          {events[0].eventName}
        </Typography>
      )}

      {threads.length === 0 && (
        <Typography sx={{ px: 2, pt: 4, fontSize: 14, color: "text.secondary" }}>
          {t("chat.list.empty")}
        </Typography>
      )}

      <List disablePadding sx={{ mt: 1 }}>
        {threads.map((thread) => (
          <ListItemButton
            key={thread._id}
            onClick={() => navigate(`/chats/${thread._id}`)}
          >
            <ListItemAvatar>
              <Badge
                color="error"
                badgeContent={thread.unread > 99 ? "99+" : thread.unread}
                invisible={!thread.unread}
              >
                <Avatar src={thread.other?.profileImage || undefined}>
                  {(thread.other?.firstName || "?").charAt(0)}
                </Avatar>
              </Badge>
            </ListItemAvatar>
            <ListItemText
              primary={nameOf(thread.other)}
              secondary={[eventName(thread), previewOf(thread)]
                .filter(Boolean)
                .join(" · ")}
              primaryTypographyProps={{
                fontWeight: thread.unread ? 700 : 500,
              }}
              secondaryTypographyProps={{ noWrap: true }}
            />
            <Typography
              sx={{ fontSize: 11, color: "text.secondary", ml: 1, flexShrink: 0 }}
            >
              {thread.lastMessageAt
                ? dayjs(thread.lastMessageAt).format("h:mm A")
                : ""}
            </Typography>
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
};

export default ThreadList;
